import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useFilters } from '../hooks/useFilters';
import SEO from '../components/SEO';
import AppSidebar from '../components/common/AppSidebar';

const AskQuestionPage = () => {
  const { practiceAreas } = useFilters();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    question: '',
    practiceArea: '',
    location: ''
  });

  const navigate = useNavigate();

  // Set data-page attribute on body for CSS targeting
  useEffect(() => {
    document.body.setAttribute('data-page', 'home');
    
    return () => {
      document.body.removeAttribute('data-page');
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/general-questions`, formData);
      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting question:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const steps = [
    {
      title: "Describe Your Legal Issue",
      description: "Provide a clear and concise description of your legal situation. The more details you provide, the better guidance you'll receive."
    },
    {
      title: "Specify Your Location",
      description: "Legal requirements can vary by location. Letting us know where you're based helps ensure relevant advice."
    },
    {
      title: "Choose Practice Area",
      description: "Select the most relevant legal practice area for your question to route it to the right specialists."
    },
    {
      title: "Get Expert Guidance",
      description: "Our network of qualified solicitors will review your question and provide professional insights and next steps."
    }
  ];

  return (
    <>
      <SEO 
        fallbackTitle="Ask a Legal Question using Zahoot"
        fallbackDescription="Have a legal question? Get help from qualified legal professionals. Submit your question and receive expert guidance on your legal matters."
      />
      <div className="home-page">
        {/* Sidebar Navigation */}
        <AppSidebar />
        
        <main className="home-page__main">
          <div className="max-w-6xl w-full px-4 lg:right-9 sm:px-6 lg:px-8 mx-auto z-10 relative">
            

            <div className="text-center mb-12 z-10 relative pt-2">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
                Ask a <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D80364] to-[#F0654B]">Legal Question</span>
              </h1>
            {/* ChatGuru Iframe Embed */}
            <div className="mb-8 pt-4">
              <iframe 
                frameBorder="0" 
                // src="http://localhost:3001/emb.html?chatbotId=genbot2025-07-10T06:09:39.269Z-10705"
                src='https://app.chatsguru.co/emb.html?chatbotId=genbot2025-09-03T06:50:44.877Z-23151'
                style={{
                  border: 'none',
                  width: '100%',
                  height: '600px',
                  borderRadius: '12px'
                }} 
                title="Chatbot"
              />
            </div>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Get free legal guidance from qualified solicitors
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="md:col-span-1">
                <div className="bg-purple-900/50 rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-bold text-white mb-6">How It Works</h2>
                  <div className="space-y-6">
                    {steps.map((step, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-8 h-8 rounded-full bg-purple-500/30 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                          <span className="text-purple-300 font-semibold">{index + 1}</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                          <p className="text-gray-300 text-sm">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="bg-purple-900/50 rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-bold text-white mb-6">Your Question</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-white text-sm font-medium mb-2">Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full bg-purple-800/40 text-white rounded-md px-4 py-2 border border-purple-700/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={formData.name}
                          onChange={handleChange}
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-white text-sm font-medium mb-2">Email</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full bg-purple-800/40 text-white rounded-md px-4 py-2 border border-purple-700/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="practiceArea" className="block text-white text-sm font-medium mb-2">Practice Area</label>
                        <select
                          id="practiceArea"
                          name="practiceArea"
                          className="w-full bg-purple-800/40 text-white rounded-md px-4 py-2 border border-purple-700/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={formData.practiceArea}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select a practice area</option>
                          {practiceAreas.map((area) => (
                            <option key={area} value={area}>
                              {area}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="location" className="block text-white text-sm font-medium mb-2">Location</label>
                        <input
                          type="text"
                          id="location"
                          name="location"
                          className="w-full bg-purple-800/40 text-white rounded-md px-4 py-2 border border-purple-700/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={formData.location}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="question" className="block text-white text-sm font-medium mb-2">Your Question</label>
                      <textarea
                        id="question"
                        name="question"
                        required
                        rows="4"
                        className="w-full bg-purple-800/40 text-white rounded-md px-4 py-2 border border-purple-700/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={formData.question}
                        onChange={handleChange}
                      />
                    </div>

                    <button 
                      type="submit" 
                      className="w-full text-white py-3 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-gradient-to-r from-[#D80364] to-[#F0654B] hover:from-[#F0654B] hover:to-[#D80364]"
                    >
                      Submit Question
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AskQuestionPage; 